import { useEffect, useState } from 'react';

/** Chrome / Edge / Android browsers fire `beforeinstallprompt` when the
 * PWA install criteria are met. We capture the event and surface a
 * `prompt()` call so the install can be triggered by user click. */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: Array<string>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

/** iOS Safari detection. iPadOS 13+ reports the same UA as macOS Safari,
 * so we ALSO check for `ontouchend` — a Mac with a real keyboard
 * doesn't have touch, an iPad does. iPhone / iPod still match `iPhone`
 * / `iPod` in the UA directly. */
const detectIOS = (): boolean => {
  if (typeof navigator === 'undefined' || typeof document === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPhone|iPod/.test(ua)) return true;
  if (/Macintosh/.test(ua) && 'ontouchend' in document) return true;
  return false;
};

/** Already running as an installed PWA. Two signals: the iOS Safari
 * `navigator.standalone` boolean, and the cross-browser
 * `display-mode: standalone` media query. */
const detectStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  if (nav.standalone === true) return true;
  if (typeof window.matchMedia === 'function') {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
  }
  return false;
};

export interface InstallPromptState {
  /** Running on an iOS device (where the native prompt API is unavailable
   *  and the user must use Share → Add to Home Screen manually). */
  isIOS: boolean;
  /** App is already installed / running in standalone mode. */
  isInstalled: boolean;
  /** `beforeinstallprompt` was captured — `install()` can fire the
   *  native install dialog. False on iOS (event never fires) and on
   *  browsers that already meet install criteria but haven't fired the
   *  event yet (or have suppressed it because the user dismissed
   *  recently). */
  canInstall: boolean;
  /** Trigger the native install prompt. No-op if `canInstall` is false. */
  install: () => Promise<void>;
}

export const useInstallPrompt = (): InstallPromptState => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState<boolean>(detectStandalone);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      // Prevent the browser's automatic mini-info-bar (Chrome) — we
      // want to surface the install via our own button so it stays in
      // the user's flow rather than appearing as a separate browser
      // chrome element they might dismiss.
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setIsInstalled(true);
    setDeferredPrompt(null);
  };

  return {
    isIOS: detectIOS(),
    isInstalled,
    canInstall: deferredPrompt !== null,
    install,
  };
};
